/**
 * Locale-Aware Navigation Hook
 *
 * Wraps react-router-dom's useNavigate() to automatically
 * prepend the current locale prefix to navigation paths.
 *
 * Usage:
 *   const navigate = useLocaleNavigate();
 *   navigate('/campaigns');       // navigates to '/ar/campaigns' when locale is 'ar'
 *   navigate(-1);                 // back navigation works unchanged
 *   navigate('/admin/dashboard'); // admin paths are not prefixed
 */

import { useCallback } from 'react';
import { useNavigate, type NavigateOptions, type To } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addLocaleToPath } from './index';

type NavigateFunction = (to: To | number, options?: NavigateOptions) => void;

export function useLocaleNavigate(): NavigateFunction {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      // Numeric navigation (go back/forward) -- pass through unchanged
      if (typeof to === 'number') {
        navigate(to);
        return;
      }

      // String paths -- add locale prefix
      if (typeof to === 'string') {
        const localizedPath = addLocaleToPath(to, i18n.language);
        navigate(localizedPath, options);
        return;
      }

      // Object-form navigation ({ pathname, search, hash })
      if (typeof to === 'object' && to.pathname) {
        const localizedTo = {
          ...to,
          pathname: addLocaleToPath(to.pathname, i18n.language),
        };
        navigate(localizedTo, options);
        return;
      }

      // Fallback
      navigate(to, options);
    },
    [navigate, i18n.language]
  );
}

export default useLocaleNavigate;
